<template src="./statoOrdini.html"></template>

<script>
import { get_statoOrdini, get_dettaglioOrdini } from "@/api/api.ordini.js";
import tabella from "@/components/ui/Table.vue";
import DialogPdf from "@/components/dialog/ordini/PdfConferma.vue";

// ── Costanti ────────────────────────────────────────────────────────────────

const SOGLIE_COLORE = [
  { min: 100, color: "green accent-4" },
  { min: 90, color: "light-blue accent-4" },
  { min: 80, color: "light-blue accent-3" },
  { min: 60, color: "light-blue accent-2" },
  { min: 40, color: "orange darken-1" },
  { min: 20, color: "amber darken-1" },
  { min: 0, color: "grey lighten-1" },
];

const HEADERS_TABELLA = [
  { text: "Pdf", align: "center", value: "Pdf", sortable: false },
  { text: "Ordine", align: "center", value: "NUM_ORD", sortable: true },
  { text: "Data Conferma", align: "center", value: "Data_ord", sortable: true },
  { text: "Conferma", align: "center", value: "ConfermaWeb", sortable: true },
  { text: "Consegna prevista", align: "center", value: "DataConsegna", sortable: true },
  { text: "Rag.Sociale", align: "left", value: "CLRAGSOC", sortable: true },
  { text: "Consegna", align: "center", value: "", sortable: true },
  { text: "", align: "center", value: "StatoPerc" },
];

// ── Helpers ordinamento ──────────────────────────────────────────────────────

/**
 * Confronta due valori generici per l'ordinamento.
 * @param {*} a
 * @param {*} b
 * @param {boolean} desc - true = decrescente
 */
function confrontaValori(a, b, desc) {
  if (a === b) return 0;
  const maggiore = a > b ? -1 : 1;
  return desc ? maggiore : -maggiore;
}

// ── Componente ───────────────────────────────────────────────────────────────

export default {
  name: "StatoOrdini",

  components: { tabella, DialogPdf },

  data() {
    return {
      // Dati tabella
      dati: [],
      headers: HEADERS_TABELLA,

      // Ricerca e paginazione
      search: "",
      page: 1,
      pageCount: 0,
      itemsPerPage: 300,
      pagination: { rowsPerPage: 300 },

      // Stato UI
      switchConsegna: true,
      loadingTabella: false,

      // Espansione riga
      expanded: [],
      selectedRow: {},
      attesaDettaglio: false,
      datiDettaglio: [],

      // Ordinamento manuale
      ordDesc: true,
      ordColumn: "NUM_ORD",
      sortBy: "NUM_ORD",
      sortDesc: false,

      // Dialog PDF
      dialogPdf: false,
      progDoc: 0,
      codCliente: "",
    };
  },

  computed: {
    userSwitch() {
      return this.$store.getters["user/getUserSwitch"];
    },

    getDati() {
      return this.dati;
    },
  },

  watch: {
    userSwitch() {
      this.caricaDati();
    },
  },

  async mounted() {
    // Attendo che Vue carichi i dati utente nello store
    await this.$functions.delay(500);
    this.caricaDati();
  },

  methods: {
    // ── Caricamento dati ───────────────────────────────────────────────────

    async caricaDati() {
      this.dati = [];
      this.datiDettaglio = [];

      const par = this.buildParamOrdini();
      const res = await get_statoOrdini(par);
      this.dati = res.data;
    },

    /** Costruisce il parametro URL per get_statoOrdini */
    buildParamOrdini() {
      const userSwitch = this.$store.getters["user/getUserSwitch"];
      let par = userSwitch.Codice
        ? `${userSwitch.Tipo}/${userSwitch.Codice}`
        : "";
      par += `?consegnati=${!this.switchConsegna}`;
      return par;
    },

    async caricaDettaglio(item) {
      this.datiDettaglio = [];
      const res = await get_dettaglioOrdini(item.Progressivo);
      this.datiDettaglio = res.data;
      this.attesaDettaglio = false;
    },

    // ── Gestione righe / espansione ────────────────────────────────────────

    visDettaglio(item) {
      this.selectedRow = item;
      this.datiDettaglio = [];
      this.attesaDettaglio = true;
    },

    rowClicked(item) {
      const staGiaAperta = this.expanded.includes(item);

      this.resetExpanded();
      this.datiDettaglio = [];

      if (staGiaAperta) return;

      this.expanded.push(item);
      setTimeout(() => this.caricaDettaglio(item), 500);
    },

    resetExpanded() {
      this.dati.forEach((el) => (el.expanded = false));
      this.expanded = [];
    },

    // ── PDF ────────────────────────────────────────────────────────────────

    apriPdf(item) {
      if (!item.Num_conferma) return;
      this.progDoc = item.Num_conferma;
      this.codCliente = item.COD_CLI;
      this.dialogPdf = true;
    },

    chiudiPdf() {
      this.dialogPdf = false;
    },

    // ── Colore progress circular ───────────────────────────────────────────

    getColorePerc(valore) {
      const soglia = SOGLIE_COLORE.find(({ min }) => valore >= min);
      return soglia ? soglia.color : "grey lighten-1";
    },

    // ── Ordinamento manuale ────────────────────────────────────────────────

    async changeSort(column) {
      // Se cambia colonna riparto da discendente; altrimenti inverto
      this.ordDesc = column !== this.ordColumn ? true : !this.ordDesc;
      this.ordColumn = column;

      if (column === "Data_ord") {
        this.dati = await this.ordinaDate(this.dati);
      } else {
        this.dati = [...this.dati].sort((a, b) =>
          confrontaValori(a[column], b[column], this.ordDesc)
        );
      }
    },

    async ordinaDate(arr) {
      return [...arr].sort((a, b) => {
        const dateA = new Date(this.$i2kFunctions.formatITDateToISO(a.Data_ord));
        const dateB = new Date(this.$i2kFunctions.formatITDateToISO(b.Data_ord));
        return confrontaValori(dateA, dateB, this.ordDesc);
      });
    },

    /** Disabilita l'ordinamento automatico di Vuetify (usiamo il nostro) */
    customSort(items) {
      return items;
    },
  },
};
</script>

<style scoped>
table.v-table thead th {
  color: #6a573e;
  font-size: 12px;
}

table.v-table thead tr {
  background: #e6bc87;
  border-bottom: 0px !important;
}

table.v-table tbody tr:last-child {
  border-bottom: 1px solid #d9d9d9;
}

table.v-table th:nth-child(1) {
  border-radius: 7px 0px 0px 7px;
}

table.v-table th:nth-last-child(1) {
  border-radius: 0px 7px 7px 0px;
}

table.v-table tbody td:not(:last-child) {
  border-right: 1px solid lightgray;
}

table.v-table tbody tr {
  cursor: pointer;
}

.colonna {
  color: #6a573e;
  font-size: 12px;
  background-color: #e6bc87;
  border-bottom: 0px !important;
}

@media (max-width: 600px) {
  .mobile-width {
    max-width: 200px !important;
    width: 100%;
  }
}

.ordine-completo {
  display: flex;
  align-items: center;
  color: #2e7d32;
  background-color: #daf7dc;
  /* simile success Vuetify */
  font-weight: 600;
  font-size: 0.95em;
  line-height: 1.2;
}

.ordine-completo .v-icon {
  font-size: 28px !important;
  margin-right: 4px !important;
}

.testo-ordine {
  text-align: left;
}
</style>